import { useState, useEffect } from 'react'
import { readAdminRows, readAdminStats } from '../lib/adminData'
import { INTEGRATIONS, STATUS, countByStatus } from '../data/integrations'

const NAVY = '#0F2A4A'
const GOLD = '#D4AF37'
const SILVER = '#64748B'
const LIGHT = '#F1F5F9'
const WHITE = '#FFFFFF'

const TABS = ['Overview', 'Races', 'Candidates', 'Beta Acceptances', 'Data Sync', 'Integrations']

// BEDDrock #723 (pre-election launch). The Control Tower is a separate,
// separately authenticated (Microsoft Entra) site, not in-app content, so
// it is an external link rather than another entry in TABS - there is
// nothing for `tab === 'Control Tower'` to render here.
const CONTROL_TOWER_URL = 'https://icy-pond-0868da60f.5.azurestaticapps.net'

// Render order groups the two that need action first. A registry that buries
// its gaps under a long list of healthy rows is a list, not a control.
const STATUS_ORDER = ['unverified', 'provisioned', 'live', 'future', 'linkOnly', 'retired']

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 20px', minWidth: 140 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: SILVER, textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: NAVY }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: SILVER, marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function TabBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
      border: 'none', background: 'none',
      color: active ? NAVY : SILVER,
      borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
      transition: 'color 0.15s',
    }}>{label}</button>
  )
}

function Badge({ children, color = SILVER, bg = LIGHT }) {
  return (
    <span style={{ fontSize: 11, background: bg, color, borderRadius: 12, padding: '2px 8px', fontWeight: 600 }}>{children}</span>
  )
}

export default function Admin() {
  const [tab, setTab] = useState('Overview')
  const [loading, setLoading] = useState(true)
  const [stateFilter, setStateFilter] = useState('')
  const [stats, setStats] = useState(null)
  const [races, setRaces] = useState([])
  const [candidates, setCandidates] = useState([])
  const [betaRows, setBetaRows] = useState([])

  const [error, setError] = useState(null)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    setError(null)
    setLoading(true)
    async function load() {
      try {
        let result
        if (tab === 'Overview') result = await readAdminStats()
        if (tab === 'Races') result = await readAdminRows('races', '*', { state: stateFilter, order: 'state' })
        if (tab === 'Candidates') result = await readAdminRows('candidates', 'id,full_name,name,state,office,party,district,district_geoid,candidate_status,opencampaign_url,fec_candidate_id', { state: stateFilter, order: 'state' })
        if (tab === 'Beta Acceptances') result = await readAdminRows('beta_agreement_acceptances', 'id,user_id,accepted_at,agreement_version', { order: 'accepted_at', ascending: false })
        if (!active) return
        if (tab === 'Overview') setStats(result)
        if (tab === 'Races') setRaces(result)
        if (tab === 'Candidates') setCandidates(result)
        if (tab === 'Beta Acceptances') setBetaRows(result)
      } catch {
        if (active) setError(`Could not load ${tab.toLowerCase()}. Check your session and database access, then retry.`)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [tab, stateFilter, retry])

  const coverage = stats ? Math.round((stats.withPos / Math.max(stats.candidates, 1)) * 100) : 0

  const TH = ({ children }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: SILVER, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: LIGHT, borderBottom: '1px solid #E2E8F0' }}>{children}</th>
  )
  const TD = ({ children, mono }) => (
    <td style={{ padding: '10px 16px', fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 12 : 14, color: mono ? SILVER : NAVY }}>{children}</td>
  )

  return (
    <div style={{ background: LIGHT, minHeight: '100vh', fontFamily: 'system-ui, sans-serif', color: NAVY }}>
      {/* Header */}
      <div style={{ background: NAVY, padding: '20px 32px' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: WHITE }}>Admin</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Manage BECivitas content for the 2026 pilot.</div>
      </div>

      {/* Tabs */}
      <div style={{ background: WHITE, borderBottom: '1px solid #E2E8F0', padding: '0 32px', display: 'flex', gap: 2, alignItems: 'center' }}>
        {TABS.map(t => <TabBtn key={t} label={t} active={tab === t} onClick={() => setTab(t)} />)}
        <a
          href={CONTROL_TOWER_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            marginLeft: 'auto', padding: '10px 18px', fontSize: 14, fontWeight: 600,
            color: SILVER, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          Control Tower <span aria-hidden="true" style={{ fontSize: 12 }}>↗</span>
        </a>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 32px' }}>

        {/* ── OVERVIEW ── */}
        {error && <div role="alert" style={{ padding: 20, color: '#B91C1C', background: WHITE }}>
          {error} <button onClick={() => setRetry(value => value + 1)}>Retry</button>
        </div>}
        {loading && <div role="status" style={{ padding: 20 }}>Loading...</div>}

        {tab === 'Overview' && !loading && !error && (
          <div>
            {stats && (
              <>
                {/* Top stats */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                  <StatCard label="Races" value={stats.races} />
                  <StatCard label="Candidates" value={stats.candidates} />
                  <StatCard label="Positions" value={stats.positions} />
                  <StatCard label="Evidence" value={stats.evidence} />
                </div>

                {/* By state */}
                <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: SILVER, textTransform: 'uppercase', marginBottom: 14 }}>Candidates by State</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {stats.byState.map(([state, count]) => (
                      <div key={state} style={{ background: LIGHT, borderRadius: 8, padding: '10px 16px', minWidth: 80, cursor: 'pointer' }}
                        onClick={() => { setStateFilter(state); setTab('Candidates') }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: SILVER, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{state}</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>{count}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Position coverage */}
                <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Position coverage</div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                    <StatCard label="Total Positions Synced" value={stats.positions} />
                    <StatCard label="Candidates with Positions" value={stats.withPos} sub={`/ ${stats.candidates}`} />
                    <StatCard label="Coverage" value={`${coverage}%`} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      ['Manage races \u2192', 'Add races before candidates.', 'Races'],
                      ['Manage candidates \u2192', 'View and filter all candidates.', 'Candidates'],
                      ['Beta acceptances \u2192', 'View user agreement log.', 'Beta Acceptances'],
                    ].map(([title, desc, goTab]) => (
                      <div key={title} onClick={() => setTab(goTab)} style={{ flex: 1, minWidth: 180, border: '1px solid #E2E8F0', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', background: LIGHT }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{title}</div>
                        <div style={{ fontSize: 13, color: SILVER }}>{desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: WHITE, borderRadius: 10, padding: 24 }}>
                  <div style={{ fontWeight: 700 }}>Candidate and position ingestion</div>
                  <p>Imports run through the server-side ingestion pipeline. Position classification currently uses keywords, not an LLM.</p>
                  <button onClick={() => setTab('Data Sync')}>View ingestion details</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── RACES ── */}
        {tab === 'Races' && !loading && !error && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <select value={stateFilter} onChange={e => { setStateFilter(e.target.value) }}
                style={{ padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 14 }}>
                <option value="">All states</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ color: SILVER, fontSize: 14 }}>{races.length} races shown</span>
            </div>
            <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr>{['State','Office','District','Election Date','Active','Phase','Preview only'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
                <tbody>
                  {races.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 ? '#FAFBFC' : WHITE }}>
                      <TD><strong>{r.state}</strong></TD>
                      <TD>{r.office}</TD>
                      <TD>{r.district || <span style={{color:SILVER}}>Statewide</span>}</TD>
                      <TD>{r.election_date ? new Date(`${r.election_date}T12:00:00`).toLocaleDateString() : <span style={{color:SILVER}}>—</span>}</TD>
                      <TD>{typeof r.is_active === 'boolean' ? (r.is_active ? 'Yes' : 'No') : 'Unknown'}</TD>
                      <TD>{r.race_phase || 'Unknown'}</TD>
                      <TD>{typeof r.preview_only === 'boolean' ? (r.preview_only ? 'Yes' : 'No') : 'Unknown'}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── CANDIDATES ── */}
        {tab === 'Candidates' && !loading && !error && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 14 }}>
                <option value="">All states</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ color: SILVER, fontSize: 14 }}>{candidates.length} candidates shown</span>
            </div>
            <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr>{['Name','State','Office','District','District key','Party','Status','OpenCampaign'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
                <tbody>
                  {candidates.map((c, i) => {
                    const party = c.party || ''
                    const partyColor = party.toLowerCase().includes('repub') ? { bg:'#FEF2F2', fg:'#B91C1C' }
                      : party.toLowerCase().includes('dem') ? { bg:'#EFF6FF', fg:'#1D4ED8' }
                      : { bg: LIGHT, fg: SILVER }
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 ? '#FAFBFC' : WHITE }}>
                        <TD><strong>{c.full_name || c.name}</strong></TD>
                        <TD>{c.state}</TD>
                        <TD>{c.office}</TD>
                        <TD>{c.district || 'Not recorded'}</TD>
                        <TD mono>{c.district_geoid || 'Not recorded'}</TD>
                        <TD><Badge color={partyColor.fg} bg={partyColor.bg}>{party || '—'}</Badge></TD>
                        <TD>{c.candidate_status ? <Badge color="#C2410C" bg="#FFF7ED">{c.candidate_status}</Badge> : <span style={{color:SILVER}}>—</span>}</TD>
                        <TD>{c.opencampaign_url
                          ? <a href={c.opencampaign_url} target="_blank" rel="noreferrer" style={{ color: GOLD, fontWeight: 600, fontSize: 12 }}>profile</a>
                          : <span style={{color:SILVER,fontSize:12}}>—</span>}</TD>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── BETA ACCEPTANCES ── */}
        {tab === 'Beta Acceptances' && !loading && !error && (
          <div>
            <div style={{ marginBottom: 16, color: SILVER, fontSize: 14 }}>{betaRows.length} agreement acceptances on record</div>
            <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr>{['User ID','Accepted At','Agreement version'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
                <tbody>
                  {betaRows.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 ? '#FAFBFC' : WHITE }}>
                      <TD mono>{r.user_id}</TD>
                      <TD>{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : '—'}</TD>
                      <TD>{r.agreement_version || 'Unknown legacy version'}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── AI SYNC ── */}
        {tab === 'Data Sync' && (
          <div style={{ background: WHITE, borderRadius: 12, padding: 32 }}>
            <h2>Candidate and position ingestion</h2>
            <p>Imports run in Azure Pipelines, separately from this dashboard. This page does not start imports.</p>
            <p>The pipeline retrieves federal candidate filings, congressional members and bills, and state legislators and votes. It writes candidates, keyword-classified positions, and source evidence.</p>
            <p>Race reference data is maintained separately. Candidate ingestion does not create races or guarantee complete ballot coverage.</p>
            <p>LLM position sync is not implemented in this pipeline. A successful run must be checked for the intended environment and district coverage.</p>
            <p>For staging, an authorized operator must explicitly choose staging and VA, and verify staging credentials before running. The pipeline defaults to production.</p>
            <a href="https://dev.azure.com/becivitas/BEC%20Apps/_build?definitionId=2" target="_blank" rel="noopener noreferrer">Open ingestion pipeline</a>
          </div>
        )}

        {/* \u2500\u2500 INTEGRATIONS \u2500\u2500 */}
        {tab === 'Integrations' && (
          <div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
              {STATUS_ORDER.map(s => (
                <StatCard key={s} label={STATUS[s].label} value={countByStatus()[s] ?? 0} />
              ))}
            </div>

            <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 20px', marginBottom: 24, fontSize: 13, color: SILVER, lineHeight: 1.6 }}>
              This registry describes implementation evidence; it does not check current API credentials, availability, or ingestion success. A
              service is labelled <strong style={{ color: NAVY }}>Live</strong> when a client module in this
              repository calls it. A configured key proves nothing on its own &mdash; see Google Civic.
              <strong style={{ color: NAVY }}> Link only</strong> rows have no public API and are permanent
              handoffs to a person; they are not backlog and will never be ticked off.
              Source: <code style={{ fontFamily: 'monospace', fontSize: 12 }}>src/data/integrations.js</code>.
            </div>

            {STATUS_ORDER.map(statusKey => {
              const rows = INTEGRATIONS.filter(i => i.status === statusKey)
              if (!rows.length) return null
              const meta = STATUS[statusKey]
              return (
                <div key={statusKey} style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <Badge color={meta.color} bg={meta.bg}>{meta.label}</Badge>
                    <span style={{ fontSize: 12, color: SILVER }}>{rows.length}</span>
                  </div>
                  <div style={{ background: WHITE, border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr><TH>Service</TH><TH>Auth</TH><TH>Purpose</TH><TH>Gap / evidence</TH></tr>
                      </thead>
                      <tbody>
                        {rows.map(i => (
                          <tr key={i.id} style={{ borderTop: '1px solid #E2E8F0', verticalAlign: 'top' }}>
                            <TD>
                              <a href={i.url} target="_blank" rel="noopener noreferrer" style={{ color: NAVY, fontWeight: 600 }}>{i.name}</a>
                              {i.userFacing && <div style={{ marginTop: 4 }}><Badge>shown to voters</Badge></div>}
                            </TD>
                            <TD mono>{i.auth}</TD>
                            <TD>{i.purpose}</TD>
                            <TD>
                              {i.gap && <div style={{ color: i.status === 'provisioned' ? '#8A6410' : SILVER, fontSize: 13 }}>{i.gap}</div>}
                              {i.evidence && <div style={{ fontFamily: 'monospace', fontSize: 11, color: SILVER, marginTop: i.gap ? 6 : 0 }}>{i.evidence}</div>}
                            </TD>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
